import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { IFlowCore, IFlowSubscriber } from '../../../modules/epflow'
import { Locale } from '../../../modules/locale'
import MainModule from '../../../modules/main'
import { AddedEventItem } from './lvc-create-contribution-add-events'

Locale.getInstance().registerKeySet('fr', 'create-contribution-recap', {
  title: 'Résumé des liens de contribution',
  description: `Vous y êtes presque! Prenez un moment pour valider les informations de vos liens de contribution.<br></br>* Si une modification est nécessaire, cliquez sur « Retour ».<br></br>* Si tout est conforme, cliquez sur « Soumettre » pour transmettre votre demande à La Vitrine.`,
  contributorLabel: 'Contributeur',
  eventsLabel: 'Événements associés à ce lien de contribution',
  submitSuccess: 'Contribution soumise avec succès!',
  eventType: 'Événement',
  exhibitionType: 'Exposition',
  seriesType: 'Série',
})

Locale.getInstance().registerKeySet('en', 'create-contribution-recap', {
  title: 'Contribution Links Summary',
  description: `You're almost there! Take a moment to confirm the information included in your linked contributions.<br></br>* If an element requires modification, click on "back".<br></br>* If all the information provided is correct, click on "Submit" to transfer your request to La Vitrine`,
  contributorLabel: 'Contributor',
  eventsLabel: 'Events linked to this Contributor.',
  submitSuccess: 'Contribution submitted successfully!',
  eventType: 'Event',
  exhibitionType: 'Exhibition',
  seriesType: 'Series',
})

export interface LvcCreateContributionRecap {
  flowGroup: string
  flowStep: string
}

export class LvcCreateContributionRecap
  extends MinzeElement
  implements MinzeElement, IFlowSubscriber
{
  private flowCore: IFlowCore | undefined

  attrs: Attrs = [
    ['flow-step', ''],
    ['flow-group', ''],
  ]

  reactive: Reactive = [
    ['contributorName', '', false],
    ['contributorType', '', false],
    ['contributionType', '', false],
    ['events', [], false],
  ]

  public events: AddedEventItem[] = []

  onReady() {
    const mainModule = MainModule.getInstance()
    mainModule?.getFlowManager()?.subscribe(this)
    this.flowCore = MainModule.getInstance().getFlowManager().getCore(this.flowGroup)
    this.rerender()
  }

  onDestroy() {
    MainModule?.getInstance()?.getFlowManager()?.unsubscribe(this)
  }

  public async deserialize(stepData: any, data: any): Promise<void> {
    // Get contributor data from previous steps
    const searchOrAddData = data?.createContributionSearchOrAdd
    const selectTypeData = data?.createContributionSelectType
    const addEventsData = data?.createContributionAddEvents

    this.contributorName = searchOrAddData?.contributorName || ''
    this.contributorType = searchOrAddData?.contributorType || ''
    this.contributionType = selectTypeData?.contributionType || ''
    // Use addedEvents instead of events as per the add-events component structure
    this.events = addEventsData?.addedEvents || []

    this.rerender()
  }

  public async serialize(): Promise<unknown> {
    return {
      contributorName: this.contributorName,
      contributorType: this.contributorType,
      contributionType: this.contributionType,
      events: this.events,
    }
  }

  public async validate(): Promise<boolean> {
    return true
  }

  onSubmitClick() {
    // Placeholder - show alert for now
    alert(Locale.getKey('create-contribution-recap.submitSuccess'))
    // TODO: Implement actual submission with confirmation popup
  }

  renderEvents(): string {
    if (!this.events?.length) {
      return `
        <lvc-box
          mt="16px"
          background="#BFF0FF"
          padding="24px"
          borderRadius="8px"
        >
          <lvc-text font-preset="small" color="#666">
            [Events list placeholder - will display linked events here]
          </lvc-text>
        </lvc-box>
      `
    }

    let index = 0
    return this.events
      .map((item: AddedEventItem) => {
        const items: string[] = []

        // If added to parent event - use lvc-event-block
        if (item.addToParentEvent) {
          // Determine event type label based on table/type
          const eventTable = item.eventData?.table || ''
          let typeLabel = Locale.getKey('create-contribution-recap.eventType')
          if (eventTable === 'exhibitionEvents' || eventTable === 'exhibitions') {
            typeLabel = Locale.getKey('create-contribution-recap.exhibitionType')
          } else if (eventTable === 'eventSeries' || eventTable === 'series') {
            typeLabel = Locale.getKey('create-contribution-recap.seriesType')
          }

          items.push(`
          <lvc-event-block
            event-data="${encodeURIComponent(JSON.stringify(item.eventData))}"
            event-name="${item.eventName}"
            event-type="${typeLabel}"
          ></lvc-event-block>
        `)
        }

        // Selected representations - use lvc-representation-block
        if (item.selectedOffers?.length) {
          item.selectedOffers.forEach((offer: any) => {
            // Add event name to offer for display
            const offerWithEventName = {
              ...offer,
              eventName: item.eventName,
            }
            items.push(`
            <lvc-representation-block
              representation-data="${encodeURIComponent(JSON.stringify(offerWithEventName))}"
              representation-index="${index++}"
              flow-group=""
              is-existing="true"
              show-type="true"
              hide-price="true"
              compact="true"
            ></lvc-representation-block>
          `)
          })
        }

        return items.join('')
      })
      .join('')
  }

  html = () => `
    <lvc-box mt="24px" textAlign="left">
      <lvc-text font-preset="titleMobileSmall">
        ${Locale.getKey('create-contribution-recap.title')}
      </lvc-text>
    </lvc-box>
    <lvc-box mt="24px" textAlign="left">
      <lvc-text font-preset="medium">
        ${Locale.getKey('create-contribution-recap.description')}
      </lvc-text>
    </lvc-box>
    
    <!-- Contributor Card -->
    <lvc-box
      mt="24px"
      background="#FFF"
      padding="16px"
      borderRadius="8px"
      border="1px solid var(--nuit-30, rgba(30, 0, 125, 0.30))"
    >
      <lvc-text font-preset="smallExtraBold" font-weight="800" text-transform="uppercase">
        ${this.contributorName || '[Contributor Name Placeholder]'}
      </lvc-text>
      <lvc-box mt="8px">
        <lvc-text font-preset="small">
          ${this.contributorType || '[Contributor Type Placeholder]'}
        </lvc-text>
      </lvc-box>
    </lvc-box>

    <!-- Events Section -->
    <lvc-box mt="24px" textAlign="left">
      <lvc-text font-preset="medium">
        ${Locale.getKey('create-contribution-recap.eventsLabel')}
      </lvc-text>
    </lvc-box>
    
    <lvc-box mt="16px" display="flex" flexDirection="column" rowGap="16px">
      ${this.renderEvents()}
    </lvc-box>
  `

  css = () => `
    :host {
      display: block;
    }
  `

  eventListeners: EventListeners = []
}
